import React, { useState, useEffect, useCallback } from "react";
import { Calendar } from "lucide-react";
import { format, addMinutes, isPast } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ServiceRequest } from "@/components/service-requests/types";
import { fetchAllRequests } from "@/components/service-requests/utils/requestUtils";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from 'react-i18next';

interface UpcomingAppointmentsProps {
  userId: string;
}

const UpcomingAppointments: React.FC<UpcomingAppointmentsProps> = ({ userId }) => {
  const [appointments, setAppointments] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [reminderLoading, setReminderLoading] = useState<string | null>(null);
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const { t, i18n } = useTranslation();

  // Get the appropriate locale for date-fns based on current language
  const getDateFnsLocale = () => {
    switch (i18n.language) {
      case 'fr':
        return fr;
      case 'en':
      default:
        return enUS;
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };

    fetchUser();
  }, []);

  useEffect(() => {
    const fetchAppointments = async () => {
      setLoading(true);
      try {
        // Only fetch if we have a valid userId
        if (!userId || userId === "") {
          console.log("No valid userId provided, skipping appointment fetch");
          setAppointments([]);
          setLoading(false);
          return;
        }
        
        console.log(`Fetching appointments for user: ${userId}`);
        const allRequests = await fetchAllRequests(userId);
        if (allRequests) {
          const upcoming = allRequests.filter(req => {
            if (!req.scheduled_at) return false;
            return !isPast(new Date(req.scheduled_at));
          })
          .sort((a, b) => {
            // Sort by scheduled date (ascending)
            const dateA = a.scheduled_at ? new Date(a.scheduled_at).getTime() : 0;
            const dateB = b.scheduled_at ? new Date(b.scheduled_at).getTime() : 0;
            return dateA - dateB;
          })
          .slice(0, 3); // Only take the first 3 upcoming appointments
          
          setAppointments(upcoming);
        }
      } catch (error) {
        console.error("Failed to fetch appointments:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: t('dashboard.failedToLoadAppointments'),
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [userId, toast, t]);

  const sendReminder = useCallback(async (appointment: ServiceRequest) => {
    try {
      setReminderLoading(appointment.id);
      
      // Create the notification in-app
      try {
        await (supabase
          .from('notifications' as any)
          .insert({
            user_id: user.id,
            type: 'reminder',
            title: 'Reminder Sent',
            message: `Automatic reminder sent to ${appointment.customer_name} for ${appointment.services.name}`,
            service_request_id: appointment.id,
            read: false
          }) as any);
      } catch (notifyError) {
        console.error("Error creating notification:", notifyError);
      }
      
      // Send the webhook notification (existing code)
      const reminderData = {
        requestId: appointment.id,
        serviceName: appointment.services.name,
        servicePrice: appointment.services.price.toString(),
        customerName: appointment.customer_name,
        customerPhone: appointment.customer_phone || "Not provided",
        customerEmail: appointment.customer_email || "Not provided",
        appointmentDate: appointment.scheduled_at,
        formattedAppointmentDate: appointment.scheduled_at ? new Date(appointment.scheduled_at).toLocaleString() : "Not scheduled",
        specialRequests: appointment.notes || "None",
        location: appointment.location || "Not specified"
      };
      
      const result = await triggerServiceWebhook('service_reminder', reminderData, user.id);
      
      if (result.success) {
        toast({
          title: t('dashboard.reminderSent'),
          description: `${t('dashboard.reminderSentTo')} ${appointment.customer_name}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: t('dashboard.reminderFailed'),
          description: t('dashboard.couldNotSendReminder'),
        });
      }
    } catch (error) {
      console.error("Error sending reminder:", error);
      toast({
        variant: "destructive",
        title: t('dashboard.reminderFailed'),
        description: t('dashboard.couldNotSendReminder'),
      });
    } finally {
      setReminderLoading(null);
    }
  }, [user?.id, toast, t]);

  const triggerServiceWebhook = async (
    type: 'service_request' | 'service_accepted' | 'service_reminder' | 'daily_summary' | 'service_reminder_test' | 'service_reschedule',
    data: any,
    userId: string
  ) => {
    try {
      console.log(`Preparing ${type} webhook data:`, data);
      
      // Fetch user profile information
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, company_name, preferred_messaging, whatsapp_number, telegram_chat_id')
        .eq('id', userId)
        .single();
      
      if (profileError) {
        console.error("Error fetching profile data:", profileError);
        throw profileError;
      }

      // Prepare standardized payload
      const webhookPayload = {
        ...data,
        event_type: type,
        timestamp: new Date().toISOString(),
        userId: userId,
        providerFirstName: profileData?.first_name || null,
        providerCompanyName: profileData?.company_name || null,
        providerTelegramChatId: profileData?.telegram_chat_id || null,
        providerPreferredMessaging: profileData?.preferred_messaging || "whatsapp",
        providerWhatsAppNumber: profileData?.whatsapp_number || null
      };
      
      console.log("Sending webhook payload:", webhookPayload);
      
      // Use the webhook URL from environment variables
      const webhookUrl = import.meta.env.VITE_NOTIFICATION_WEBHOOK_URL;
      
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Webhook error (${response.status}):`, errorText);
        throw new Error(`Webhook error: ${response.status} - ${errorText}`);
      }
      
      const responseData = await response.text();
      console.log("Webhook response:", responseData);
      
      return { success: true, response: responseData };
    } catch (error) {
      console.error("Error triggering service webhook:", error);
      toast({
        variant: "destructive",
        title: t('dashboard.notificationError'),
        description: t('dashboard.failedToSendNotification'),
      });
      return { success: false, error: error.message || "Unknown error" };
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
      <h2 className="text-xl font-semibold flex items-center mb-4">
        <Calendar className="h-5 w-5 mr-2" /> 
        {t('dashboard.upcomingAppointments')}
      </h2>
      
      <div className="space-y-4">
        {loading ? (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : appointments.length === 0 ? (
          <div className="text-gray-500 p-4 text-center">
            {t('dashboard.noUpcomingAppointments')}
          </div>
        ) : (
          appointments.map((appointment) => (
            <div 
              key={appointment.id} 
              className="border border-gray-200 rounded-lg p-4"
            >
              <h3 className="text-lg font-semibold text-center">
                {appointment.services.name}
              </h3>
              <p className="text-gray-500 text-center mt-1 text-sm">
                {appointment.customer_name}
              </p>
              
              {appointment.scheduled_at && (
                <>
                  <p className="text-center my-3">
                    {format(new Date(appointment.scheduled_at), "MMMM d, yyyy", { locale: getDateFnsLocale() })}
                  </p>
                  
                  <div className="flex justify-center items-center my-3">
                    <div className="text-center">
                      <span className="block text-gray-500 text-xs">{t('dashboard.starts')}</span>
                      <span className="block font-medium">
                        {format(new Date(appointment.scheduled_at), "h:mm a", { locale: getDateFnsLocale() })}
                      </span>
                    </div>
                    
                    <div className="mx-3 text-gray-300">|</div>
                    
                    <div className="text-center">
                      <span className="block text-gray-500 text-xs">{t('dashboard.ends')}</span>
                      <span className="block font-medium">
                        {format(
                          addMinutes(
                            new Date(appointment.scheduled_at), 
                            appointment.services.duration || 60
                          ), 
                          "h:mm a",
                          { locale: getDateFnsLocale() }
                        )}
                      </span>
                    </div>
                  </div>
                </>
              )}
              
              <button 
                className="w-full border border-gray-300 rounded-md py-2 px-4 mt-3 text-gray-800 font-medium hover:bg-gray-50 transition-colors"
                onClick={() => sendReminder(appointment)}
                disabled={reminderLoading === appointment.id}
              >
                {reminderLoading === appointment.id ? t('dashboard.sending') : t('dashboard.sendReminder')}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default UpcomingAppointments;
